import React, { useEffect, useRef, useState } from "react";
import { Buildings, MapPin, Package, UsersThree } from "@phosphor-icons/react";
import "./animated-stats.css";

export const growthStats = [
  {
    label: "Targeting Customers",
    value: 1,
    suffix: "M+",
    caption: "Across UAE & India",
    icon: UsersThree,
    tone: "green",
  },
  {
    label: "Targeting Merchants",
    value: 50,
    suffix: "k+",
    caption: "Businesses to onboard",
    icon: Buildings,
    tone: "pink",
  },
  {
    label: "Targeting Orders",
    value: 80,
    suffix: "M+",
    caption: "Annual orders goal",
    icon: Package,
    tone: "orange",
  },
  {
    label: "Covering Cities",
    value: 60,
    suffix: "+",
    caption: "Expansion roadmap",
    icon: MapPin,
    tone: "blue",
  },
];

const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

const getNow = () => {
  if (typeof performance !== "undefined" && performance.now) {
    return performance.now();
  }

  return Date.now();
};

const formatNumber = (value) => {
  try {
    return new Intl.NumberFormat("en-US").format(value);
  } catch (error) {
    return String(value);
  }
};

const AnimatedNumber = ({ duration = 1400, started, suffix = "", value }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!started) {
      setDisplayValue(0);
      return undefined;
    }

    const shouldReduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (shouldReduceMotion || typeof requestAnimationFrame === "undefined") {
      setDisplayValue(value);
      return undefined;
    }

    let frameId;
    const startedAt = getNow();

    const tick = () => {
      const elapsed = getNow() - startedAt;
      const progress = Math.min(elapsed / duration, 1);
      const nextValue = Math.round(value * easeOutCubic(progress));

      setDisplayValue(nextValue);

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [duration, started, value]);

  return (
    <>
      {formatNumber(displayValue)}
      {suffix}
    </>
  );
};

const AnimatedStats = ({
  className = "",
  eyebrow = "Built for scale",
  items = growthStats,
  subtitle = "AI-powered commerce built to connect customers, merchants, and delivery partners across the UAE and India.",
  title = (
    <>
      Our Growth <em>Targets</em>
    </>
  ),
}) => {
  const sectionRef = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section || typeof IntersectionObserver === "undefined") {
      setStarted(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section
      className={`animated-stats ${className}`.trim()}
      ref={sectionRef}
    >
      <div className="container">
        <div className="animated-stats__heading">
          <span>{eyebrow}</span>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>

        <div className="animated-stats__panel" aria-label="RGHT NOW growth targets">
          {items.map((item) => {
            const Icon = item.icon;

            return (
              <article
                className={`animated-stats__card animated-stats__card--${item.tone}`}
                key={item.label}
              >
                <span className="animated-stats__icon" aria-hidden="true">
                  <Icon size={34} weight="fill" />
                </span>
                <div>
                  <p>{item.label}</p>
                  <strong>
                    <AnimatedNumber
                      started={started}
                      suffix={item.suffix}
                      value={item.value}
                    />
                  </strong>
                  <small>{item.caption}</small>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AnimatedStats;
